import { useMemo } from "react"
import { IMember, IPresence } from "./types"
import { useTranslation } from "react-i18next"
import { useEventId } from ".."
import { useEvent } from "../queries"
import { useExecutionMembers, useMembers } from "./queries"
import { usePopup } from "utils/use-popup"
import Main from "modules/layout/dashboard/main"
import { Button, Input, Select, Typography } from "antd"
import Loader from "components/loader"
import MemberItem from "components/member-item"
import styles from "./styles.module.scss"
import SubscribeMember from "./subscribe"
import FeedbackDrawer from "./feedback"
import PresenceDrawer from "./presence"
import { ReactComponent as MemberIcon } from "assets/icons/member.svg"
import { ReactComponent as AddMemberIcon } from "assets/icons/add-members.svg"
import UnsubscribeMemberModal from "./unsubscribe"
import useWindowDimensions from "utils/use-window-dimension"
import { useNavigate, useSearchParams } from "react-router-dom"
import PlannedAbsenceModal from "./plannedAbsenceModal"
import BathroomHelpModal from "./bathroomHelp"

const { Title } = Typography

const MEMBER_PARAM = "member"
const STATUS_PARAM = "status"

const Members: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { width } = useWindowDimensions()
  let [searchParams, setSearchParams] = useSearchParams()
  const presenceDrawer = usePopup<{
    transition: string
    presenceId: string
    member: IMember
  }>()
  const subscribleMemberDrawer = usePopup()
  const unsubscribleMemberModal = usePopup<string>()
  const plannedAbsenceModal = usePopup<string>()
  const feedbackDrawer = usePopup<IPresence>()
  const bathroomHelpModal = usePopup<string>()
  const eventId = useEventId()
  const event = useEvent(eventId!)
  const members = useMembers(eventId!, {
    enabled: event.data?.status === "approved" && event.isFetched,
  })

  const membersExecution = useExecutionMembers(eventId!, {
    enabled: event.data?.status === "execution" && event.isFetched,
  })

  const displayedMembers = useMemo(
    () => (event.data?.status === "execution" ? membersExecution : members),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [members, membersExecution]
  )
  const isLoading = event.isLoading || displayedMembers.isLoading

  const handleStatusChange = (selected: string[]) => {
    searchParams.delete(STATUS_PARAM)
    selected.forEach((value) => {
      if (!searchParams.getAll(STATUS_PARAM).includes(value)) {
        searchParams.append(STATUS_PARAM, value)
      }
    })
    setSearchParams(searchParams, { replace: true })
  }

  return (
    <>
      <Main.PageHeader
        onButtonClick={() => navigate("../..")}
        title={<Title level={4}>{event.data?.name}</Title>}
        rightContent={
          <div style={{ display: "flex", gap: 8 }}>
            {width! > 804 && event.data?.status === "execution" && (
              <Select
                mode="multiple"
                showSearch={false}
                allowClear
                style={{ width: 312 }}
                size="large"
                placeholder={t("labels.status")}
                options={[
                  {
                    value: "planned",
                    label: t("members.planned"),
                  },
                  {
                    value: "checkin",
                    label: t("members.checkin"),
                  },
                  {
                    value: "checkout",
                    label: t("members.checkout"),
                  },
                ]}
                value={searchParams.getAll(STATUS_PARAM)}
                onChange={handleStatusChange}
              />
            )}
            {event.data?.status === "execution" && (
              <Button
                size="large"
                type="primary"
                className={styles.addMemberButton}
                loading={event.isLoading}
                onClick={subscribleMemberDrawer.open}
              >
                <AddMemberIcon
                  style={width! > 520 ? { marginRight: 10 } : {}}
                />
                {width! > 520 && t("members.add_member")}
              </Button>
            )}
          </div>
        }
      />
      {width! <= 804 && event.data?.status === "execution" && (
        <div className={styles.searchInputContainer}>
          <Select
            mode="multiple"
            showSearch={false}
            allowClear
            style={{ width: "100%" }}
            size="large"
            placeholder={t("labels.status")}
            options={[
              {
                value: "planned",
                label: t("members.planned"),
              },
              {
                value: "checkin",
                label: t("members.checkin"),
              },
              {
                value: "checkout",
                label: t("members.checkout"),
              },
            ]}
            value={searchParams.getAll(STATUS_PARAM)}
            onChange={handleStatusChange}
          />
        </div>
      )}
      <div className={styles.searchInputContainer}>
        <Input
          className={styles.searchInput}
          size="large"
          placeholder={t("global.search")}
          value={searchParams.get(MEMBER_PARAM) ?? ""}
          onChange={(e) => {
            e.target.value
              ? searchParams.set(MEMBER_PARAM, e.target.value)
              : searchParams.delete(MEMBER_PARAM)
            setSearchParams(searchParams, { replace: true })
          }}
        />
      </div>
      <div className={styles.listContainer}>
        {isLoading ? (
          <Loader />
        ) : displayedMembers.data?.length! > 0 ? (
          displayedMembers.data
            ?.filter((member) =>
              event.data?.status === "execution"
                ? member.member.name
                    .toLowerCase()
                    .includes(
                      searchParams.get(MEMBER_PARAM)?.toLowerCase() ?? ""
                    ) &&
                  (searchParams.getAll(STATUS_PARAM).length
                    ? searchParams.getAll(STATUS_PARAM).includes(member.status)
                    : true)
                : (member as any).name
                    .toLowerCase()
                    .includes(
                      searchParams.get(MEMBER_PARAM)?.toLowerCase() ?? ""
                    )
            )
            .map((item) => (
              <MemberItem
                key={item.id}
                item={item}
                unsubscribeMemberModal={unsubscribleMemberModal.open}
                plannedAbsenceModal={plannedAbsenceModal.open}
                presenceDrawer={presenceDrawer}
                isLoading={displayedMembers.isLoading}
                feedbackDrawer={feedbackDrawer}
                bathroomHelpModal={bathroomHelpModal.open}
              />
            ))
        ) : (
          <div className={styles.emptyRenderer}>
            <MemberIcon height={35} />
            <Title level={4}>
              {t("global.no_x_found", {
                entity: t("members.member"),
                context: "male",
                count: 1,
              })}
            </Title>
          </div>
        )}
      </div>
      <SubscribeMember
        {...subscribleMemberDrawer}
        open={subscribleMemberDrawer.isOpen}
      />
      <UnsubscribeMemberModal
        {...unsubscribleMemberModal}
        open={unsubscribleMemberModal.isOpen}
      />
      <PresenceDrawer {...presenceDrawer} open={presenceDrawer.isOpen} />
      <FeedbackDrawer {...feedbackDrawer} open={feedbackDrawer.isOpen} />
      <PlannedAbsenceModal
        {...plannedAbsenceModal}
        open={plannedAbsenceModal.isOpen}
      />
      <BathroomHelpModal
        {...bathroomHelpModal}
        open={bathroomHelpModal.isOpen}
      />
    </>
  )
}
export default Members
